import { ConsoleApiClient, EndpointReturnType } from '@cast/api-client';

import { FixtureReader, FixtureReaders, Locator } from '../types';
import { buildFileName, LoadFixtureArgs, makeFixtureLoader } from './_utils';
import type { FileLoaders, LoaderLocator } from './types';

// Wildcard glob patterns at module scope (evaluated once at build time)
// Use ../fixtures/*/ pattern to match any fixture group dynamically

// State-level static JSON files
const allStateJson = import.meta.glob<{ default: unknown }>(
  '../fixtures/*/states/*/static/*.json',
  { eager: false }
);

// State-level consolidated readers
const allStateReaders = import.meta.glob<{ default: FixtureReaders }>(
  '../fixtures/*/states/*/readers.ts',
  { eager: false }
);

// Root-level static JSON files
const allRootStaticJson = import.meta.glob<{ default: unknown }>(
  '../fixtures/*/static/*.json',
  { eager: false }
);

// Root-level consolidated readers
const allRootReaders = import.meta.glob<{ default: FixtureReaders }>(
  '../fixtures/*/readers.ts',
  { eager: false }
);

export type FixturesGroup = string;

// Helper to filter glob results by fixture group
const filterByGroup = <T>(
  globResult: Record<string, T>,
  group: string
): Record<string, T> => {
  const prefix = `../fixtures/${group}/`;
  return Object.fromEntries(
    Object.entries(globResult).filter(([key]) => key.startsWith(prefix))
  );
};

// Build glob key paths (relative from this file)
const buildStateStaticJsonPath = (
  group: FixturesGroup,
  state: string,
  fileName: string
): string => `../fixtures/${group}/states/${state}/static/${fileName}.json`;

const buildStateReadersPath = (group: FixturesGroup, state: string): string =>
  `../fixtures/${group}/states/${state}/readers.ts`;

const buildRootStaticJsonPath = (
  group: FixturesGroup,
  fileName: string
): string => `../fixtures/${group}/static/${fileName}.json`;

const buildRootReadersPath = (group: FixturesGroup): string =>
  `../fixtures/${group}/readers.ts`;

/**
 * FixtureLoader provides methods to load fixtures from browser environment.
 * Uses import.meta.glob for dynamic module loading at build time.
 */
export type FixtureLoader = {
  /**
   * The underlying file loaders used by this loader.
   * Exposed for use with makeFixtureLoader in other contexts.
   */
  fileLoaders: FileLoaders;
  loadStateJson: <
    FEATURE extends keyof ConsoleApiClient,
    ENDPOINT extends keyof ConsoleApiClient[FEATURE]
  >(
    locator: LoaderLocator<FEATURE, ENDPOINT>
  ) => Promise<EndpointReturnType<FEATURE, ENDPOINT> | null>;
  loadStateReader: (locator: LoaderLocator) => Promise<FixtureReader | null>;
  loadStaticJson: <
    FEATURE extends keyof ConsoleApiClient,
    ENDPOINT extends keyof ConsoleApiClient[FEATURE]
  >(
    locator: LoaderLocator<FEATURE, ENDPOINT>
  ) => Promise<EndpointReturnType<FEATURE, ENDPOINT> | null>;
  loadStaticReader: (locator: LoaderLocator) => Promise<FixtureReader | null>;
  loadFixture: <
    FEATURE extends keyof ConsoleApiClient,
    ENDPOINT extends keyof ConsoleApiClient[FEATURE]
  >(
    args: LoadFixtureArgs<FEATURE, ENDPOINT>
  ) => Promise<EndpointReturnType<FEATURE, ENDPOINT> | undefined>;
};

export const makeLoader = (fixturesGroup: FixturesGroup): FixtureLoader => {
  // Filter glob results for this specific group
  const modules = {
    stateJson: filterByGroup(allStateJson, fixturesGroup),
    stateReaders: filterByGroup(allStateReaders, fixturesGroup),
    rootStaticJson: filterByGroup(allRootStaticJson, fixturesGroup),
    rootReaders: filterByGroup(allRootReaders, fixturesGroup),
  };

  // Helper to get a specific reader from a consolidated readers.ts file
  // Supports asterisk readers: feature-level ('*') and root-level ('*')
  const getReaderFromModule = (
    readersModule: FixtureReaders,
    locator: Locator
  ): FixtureReader | null => {
    // Handle root-level asterisk reader
    if (locator.feature === '*') {
      const rootAsterisk = readersModule['*' as keyof typeof readersModule];
      if (rootAsterisk && typeof rootAsterisk === 'function') {
        return rootAsterisk as FixtureReader;
      }
      return null;
    }

    const featureReaders =
      readersModule[locator.feature as keyof typeof readersModule];
    if (!featureReaders) {
      return null;
    }

    // Handle feature-level asterisk reader
    if (locator.endpoint === '*') {
      const featureAsterisk = (featureReaders as any)['*'];
      if (featureAsterisk && typeof featureAsterisk === 'function') {
        return featureAsterisk as FixtureReader;
      }
      return null;
    }

    const reader =
      featureReaders[locator.endpoint as keyof typeof featureReaders];
    if (!reader || typeof reader !== 'function') {
      return null;
    }
    return reader;
  };

  // Helper to create a full Locator from LoaderLocator
  const toFullLocator = <
    FEATURE extends keyof ConsoleApiClient,
    ENDPOINT extends keyof ConsoleApiClient[FEATURE]
  >(
    loaderLocator: LoaderLocator<FEATURE, ENDPOINT>
  ): Locator<FEATURE, ENDPOINT> => ({
    ...loaderLocator,
    fixturesGroup,
  });

  // Define methods as standalone functions so loadFixture can reuse them
  const loadStateJson = async <
    FEATURE extends keyof ConsoleApiClient,
    ENDPOINT extends keyof ConsoleApiClient[FEATURE]
  >(
    loaderLocator: LoaderLocator<FEATURE, ENDPOINT>
  ): Promise<EndpointReturnType<FEATURE, ENDPOINT> | null> => {
    if (!loaderLocator.state) {
      return null;
    }
    const locator = toFullLocator(loaderLocator);
    const path = buildStateStaticJsonPath(
      fixturesGroup,
      locator.state!,
      buildFileName(locator)
    );
    const moduleLoader = modules.stateJson[path];
    if (!moduleLoader) {
      return null;
    }
    const module = await moduleLoader();
    return (module.default ?? module) as EndpointReturnType<FEATURE, ENDPOINT>;
  };

  const loadStateReader = async (
    loaderLocator: LoaderLocator
  ): Promise<FixtureReader | null> => {
    if (!loaderLocator.state) {
      return null;
    }
    const locator = toFullLocator(loaderLocator);
    const path = buildStateReadersPath(fixturesGroup, locator.state!);
    const moduleLoader = modules.stateReaders[path];
    if (!moduleLoader) {
      return null;
    }
    const module = await moduleLoader();
    return getReaderFromModule(module.default, locator);
  };

  const loadStaticJson = async <
    FEATURE extends keyof ConsoleApiClient,
    ENDPOINT extends keyof ConsoleApiClient[FEATURE]
  >(
    loaderLocator: LoaderLocator<FEATURE, ENDPOINT>
  ): Promise<EndpointReturnType<FEATURE, ENDPOINT> | null> => {
    const locator = toFullLocator(loaderLocator);
    const path = buildRootStaticJsonPath(fixturesGroup, buildFileName(locator));
    const moduleLoader = modules.rootStaticJson[path];
    if (!moduleLoader) {
      return null;
    }
    const module = await moduleLoader();
    return (module.default ?? module) as EndpointReturnType<FEATURE, ENDPOINT>;
  };

  const loadStaticReader = async (
    loaderLocator: LoaderLocator
  ): Promise<FixtureReader | null> => {
    const locator = toFullLocator(loaderLocator);
    const path = buildRootReadersPath(fixturesGroup);
    const moduleLoader = modules.rootReaders[path];
    if (!moduleLoader) {
      return null;
    }
    const module = await moduleLoader();
    return getReaderFromModule(module.default, locator);
  };

  // Load arbitrary JSON file (not tied to endpoint typing)
  const loadJson = async <T = unknown>(args: {
    fileName: string;
    state?: string;
    tag?: string;
  }): Promise<T | null> => {
    const fileNameWithTag = args.tag
      ? `${args.fileName}.${args.tag}`
      : args.fileName;

    // Try state-specific location first if state is provided
    if (args.state) {
      const statePath = buildStateStaticJsonPath(
        fixturesGroup,
        args.state,
        fileNameWithTag
      );
      const stateModuleLoader = modules.stateJson[statePath];
      if (stateModuleLoader) {
        const module = await stateModuleLoader();
        return (module.default ?? module) as T;
      }
    }

    // Fall back to root static location
    const rootPath = buildRootStaticJsonPath(fixturesGroup, fileNameWithTag);
    const rootModuleLoader = modules.rootStaticJson[rootPath];
    if (rootModuleLoader) {
      const module = await rootModuleLoader();
      return (module.default ?? module) as T;
    }

    return null;
  };

  // Create FileLoaders object from the individual load methods
  const fileLoaders: FileLoaders = {
    loadStateJson,
    loadStateReader,
    loadStaticJson,
    loadStaticReader,
    loadJson,
  };

  // Use the shared makeFixtureLoader instead of inline implementation
  const loadFixture = makeFixtureLoader(fileLoaders);

  return {
    fileLoaders,
    loadStateJson,
    loadStateReader,
    loadStaticJson,
    loadStaticReader,
    loadFixture,
  };
};
